/**
 * Choosing a drink.
 *
 * Three option groups with different arities, one of which changes the line's
 * SKU. The old modal was 546 lines and most of them were arithmetic: it
 * computed a display price, `kiosk-order-store.ts` summed a cart, and
 * `checkout-api.ts` reconstructed a base price by subtracting the change option
 * and every add-on back out — three derivations of one number.
 *
 * Here the *selection* is the state and `priceLine` is the only arithmetic, so
 * this file is layout.
 *
 * **The sheet sizes to the product, and 88vh is the ceiling.** A drink with
 * three shots is a short confirmation; a latte with every extra scrolls
 * behind a pinned "add". Forcing 88vh for either left a blank page under
 * the options or a lonely photo filling leftover height.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { defaultTemperature, format, imageFor, needsTemperatureChoice, priceLine } from '@mgc/domain'
import type { Product, Sku, Temperature } from '@mgc/domain'
import { Icon } from '@/components/icons'
import { cancelFlight, catchBasket, flyIntoBasket } from '@/components/fly-into-basket'
import { Button, Notice, Plaque, ProductImage, Sheet, Stepper, TemperatureTag } from '@/components/ui'
import { useRefusal, useT } from '@/i18n'
import { useKiosk } from '@/store/kiosk'

export function ProductModal() {
  const t = useT()
  const product = useKiosk((s) => s.openProduct)
  const close = useKiosk((s) => s.openProductModal)
  const add = useKiosk((s) => s.add)

  if (product === null) return null
  return <Body key={product.id} product={product} onClose={() => close(null)} onAdd={add} t={t} />
}

function Body({
  product,
  onClose,
  onAdd,
  t
}: {
  product: Product
  onClose: () => void
  onAdd: ReturnType<typeof useKiosk.getState>['add']
  t: (key: Parameters<ReturnType<typeof useT>>[0]) => string
}) {
  const mustChoose = needsTemperatureChoice(product)
  const [temperature, setTemperature] = useState<Temperature | undefined>(defaultTemperature(product))
  const [changeCode, setChangeCode] = useState<Sku | undefined>(undefined)
  const [addCodes, setAddCodes] = useState<Sku[]>([])
  const [quantity, setQuantity] = useState(1)
  /*
   * Why the refusal is held here at all.
   *
   * `add()` has always returned `{ ok, reason }` — the store built it that way
   * so a domain refusal could reach a screen — and this modal threw the result
   * away. The visible failure was a drink that sold out between the tile and
   * the "add" button: the press did nothing, the sheet stayed open, and the
   * customer had a dead control and no account of it. Cleared on every change
   * to the selection, because a refusal is about the thing that was tried and
   * not about the sheet.
   */
  const [refused, setRefused] = useState<string | null>(null)
  const refusalText = useRefusal()
  useEffect(() => {
    setRefused(null)
  }, [temperature, changeCode, addCodes, quantity])

  /** The live price, from the same function the server will run. */
  const preview = useMemo(
    () =>
      priceLine(product, {
        id: 'preview',
        productId: product.id,
        quantity,
        ...(temperature === undefined ? {} : { temperature }),
        ...(changeCode === undefined ? {} : { changeCode }),
        addCodes
      }),
    [product, quantity, temperature, changeCode, addCodes]
  )

  const shots = product.addOns.filter((a) => a.group === 'shots')
  const extras = product.addOns.filter((a) => a.group === 'extras')
  const blocked = mustChoose && temperature === undefined
  const choosing = mustChoose || product.changeOptions.length > 0 || shots.length > 0 || extras.length > 0

  /**
   * The name at the top of the sheet.
   *
   * Truthy and not `!== undefined`, which is what it was: the feed sends
   * `iceName: ''` for a product that has no separate iced name, so every
   * ice-only drink — most of the cold half of the menu — opened a sheet with
   * **no title at all**. `line.ts:label` has always used the truthy test; this
   * is the same rule in the one other place that makes the same choice.
   */
  const title = (temperature === 'ice' && product.iceName ? product.iceName : product.name) || product.nameEn || ''

  const toggle = (code: Sku) =>
    setAddCodes((codes) => (codes.includes(code) ? codes.filter((c) => c !== code) : [...codes, code]))

  /*
   * The shots group is a single choice without a quantity. «Add a shot» and
   * «remove a shot» are alternatives — a cup with both is a ticket the barista
   * cannot make — so choosing one clears every other shot, and choosing the
   * chosen one again clears it. Extras are untouched.
   */
  const shotCodes = new Set(shots.map((s) => s.code))
  const chooseShot = (code: Sku) =>
    setAddCodes((codes) => [...codes.filter((c) => !shotCodes.has(c)), ...(codes.includes(code) ? [] : [code])])

  /*
   * The cup photographs are portrait in a wide band, so `object-contain` is
   * height-limited. Compact is `h-96`; grow to `40rem` when 88vh still has
   * slack after the options have taken their height — not into leftover 88vh,
   * which is how the lonely giant came back.
   */
  const heroRef = useRef<HTMLDivElement>(null)
  const [roomy, setRoomy] = useState(false)
  useLayoutEffect(() => {
    const sheet = heroRef.current?.closest('[role="dialog"]')
    if (!(sheet instanceof HTMLElement)) return
    const leftover = window.innerHeight * 0.88 - sheet.getBoundingClientRect().height
    const scroller = sheet.querySelector<HTMLElement>('[data-product-options]')
    const scrolling = scroller !== null && scroller.scrollHeight > scroller.clientHeight + 2
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize)
    const step = 16 * rem
    if (roomy && (scrolling || leftover < 8)) setRoomy(false)
    else if (!roomy && !scrolling && leftover > step + 16) setRoomy(true)
  }, [roomy, choosing])

  return (
    <Sheet open onClose={onClose} labelledBy="product-name" width="wide" height="tall">
      {/*
        The hero. The picture is what the customer tapped and it should still be
        the biggest thing on the screen once the sheet is open — it was a 192px
        thumbnail beside the name, which is a search result, not a product.
      */}
      <div ref={heroRef} className="relative shrink-0 bg-[var(--color-paper-sunk)]">
        <ProductImage
          src={imageFor(product, temperature)}
          name={product.name}
          fit="contain"
          className={`w-full ${roomy ? 'h-[40rem]' : 'h-96'}`}
        />
        <button type="button" onClick={onClose} aria-label={t('common.back')} className="tap absolute top-6 right-6">
          <Plaque icon="close" tone="paper" size="small" className="shadow-[var(--shadow-lift)]" />
        </button>
        {temperature !== undefined && (
          <TemperatureTag
            temperature={temperature}
            label={temperature === 'ice' ? t('product.ice') : t('product.hot')}
            size="label"
            className="absolute bottom-6 left-10 shadow-[var(--shadow-lift)]"
          />
        )}
      </div>

      <div className="flex shrink-0 items-end justify-between gap-8 border-b border-[var(--color-line)] px-10 py-7">
        <div className="min-w-0 flex-1">
          <h2 id="product-name" className="display truncate text-[length:var(--text-kiosk-xl)]">
            {title}
          </h2>
          {product.nameEn && product.nameEn !== title && <p className="eyebrow mt-3">{product.nameEn}</p>}
        </div>
        <p className="display shrink-0 text-[length:var(--text-kiosk-lg)] text-[var(--color-brand-strong)]">
          {format(product.discountedPrice ?? product.price)}
        </p>
      </div>

      {choosing && (
        <div data-product-options className="flex-1 space-y-10 overflow-y-auto overscroll-contain p-10">
          {mustChoose && (
            <Group title={t('product.chooseTemperature')} required={temperature === undefined}>
              <div className="grid w-full grid-cols-2 gap-6">
                <Slab
                  selected={temperature === 'hot'}
                  onSelect={() => setTemperature('hot')}
                  label={t('product.hot')}
                  glyph="🔥"
                  accent="var(--color-hot)"
                />
                <Slab
                  selected={temperature === 'ice'}
                  onSelect={() => setTemperature('ice')}
                  label={t('product.ice')}
                  glyph="🧊"
                  accent="var(--color-ice)"
                />
              </div>
            </Group>
          )}

          {product.changeOptions.length > 0 && (
            <Group title={t('product.change')}>
              <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-6">
                <Slab
                  selected={changeCode === undefined}
                  onSelect={() => setChangeCode(undefined)}
                  label={t('product.sizeStandard')}
                  glyph="🥤"
                />
                {product.changeOptions.map((option) => (
                  <Slab
                    key={option.code}
                    selected={changeCode === option.code}
                    onSelect={() => setChangeCode(option.code)}
                    label={option.label}
                    glyph="🥤"
                    price={option.price}
                  />
                ))}
              </div>
            </Group>
          )}

          {/*
          `t('product.shots')`, not `shots[0].label`. The group used to be
          titled by its own first option, so the heading read "Add 1 Shot" over
          a list whose first row also read "Add 1 Shot" — the customer was being
          asked to choose between a thing and itself.
        */}
          {shots.length > 0 && (
            <Group title={t('product.shots')}>
              <div role="radiogroup" aria-label={t('product.shots')} className="flex w-full flex-col gap-4">
                {shots.map((shot) => {
                  const on = addCodes.includes(shot.code)
                  return (
                    <button
                      key={shot.code}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => chooseShot(shot.code)}
                      className={`tap flex w-full items-center justify-between gap-4 rounded-[var(--radius-card)] border-4 px-7 py-5 text-left ${
                        on
                          ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)]'
                          : 'border-[var(--color-line)] bg-[var(--color-paper-raised)]'
                      }`}
                    >
                      <span className="display-upright min-w-0">
                        {shot.label}{' '}
                        {shot.price > 0n && (
                          <span className="text-[var(--color-brand-strong)]">+{format(shot.price)}</span>
                        )}
                      </span>
                      <span
                        className={`grid size-14 shrink-0 place-items-center rounded-full ${
                          on
                            ? 'bg-[var(--color-brand)] text-[var(--color-brand-ink)]'
                            : 'bg-[var(--color-paper-sunk)] text-transparent'
                        }`}
                      >
                        <Icon name="check" className="size-8" />
                      </span>
                    </button>
                  )
                })}
              </div>
            </Group>
          )}

          {extras.length > 0 && (
            <Group title={t('product.extras')}>
              <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] gap-5">
                {extras.map((extra) => {
                  const on = addCodes.includes(extra.code)
                  return (
                    <button
                      key={extra.code}
                      type="button"
                      onClick={() => toggle(extra.code)}
                      aria-pressed={on}
                      className={`tap flex items-center justify-between gap-4 rounded-[var(--radius-card)] border-4 px-6 py-5 text-left ${
                        on
                          ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)]'
                          : 'border-[var(--color-line)] bg-[var(--color-paper-raised)]'
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="display-upright block">{extra.label}</span>
                        {extra.price > 0n && (
                          <span className="display-upright text-[var(--color-brand-strong)]">
                            +{format(extra.price)}
                          </span>
                        )}
                      </span>
                      <span
                        className={`grid size-14 shrink-0 place-items-center rounded-full ${
                          on
                            ? 'bg-[var(--color-brand)] text-[var(--color-brand-ink)]'
                            : 'bg-[var(--color-paper-sunk)] text-transparent'
                        }`}
                      >
                        <Icon name="check" className="size-8" />
                      </span>
                    </button>
                  )
                })}
              </div>
            </Group>
          )}
        </div>
      )}

      {refused !== null && (
        <div className="shrink-0 px-10 pb-2">
          <Notice tone="bad">{refusalText(refused)}</Notice>
        </div>
      )}

      <div className="flex shrink-0 items-center gap-8 border-t border-[var(--color-line)] bg-[var(--color-paper-raised)] px-10 py-8">
        <div className="min-w-0">
          <p className="eyebrow">{t('cart.total')}</p>
          <p className="display text-[length:var(--text-kiosk-xl)]">{format(preview.total)}</p>
        </div>

        <Stepper quantity={quantity} onChange={(next) => setQuantity(Math.max(1, Math.min(20, next)))} />

        <Button
          size="large"
          className="ml-auto uppercase"
          disabled={blocked}
          onClick={() => {
            const flew = flyIntoBasket(heroRef.current, imageFor(product, temperature))
            const result = onAdd({
              productId: product.id,
              quantity,
              ...(temperature === undefined ? {} : { temperature }),
              ...(changeCode === undefined ? {} : { changeCode }),
              addCodes
            })
            // On success the store closes the sheet itself. The cup is already
            // on the fly layer, so it keeps going into the basket. On refusal
            // the flight is cancelled and this is the only thing that says so.
            if (!result.ok) {
              cancelFlight()
              setRefused(result.reason ?? null)
              return
            }
            if (!flew) catchBasket()
          }}
        >
          {t('product.add')}
          <Icon name="cart" className="size-9" />
        </Button>
      </div>
    </Sheet>
  )
}

function Group({ title, required, children }: { title: string; required?: boolean; children: React.ReactNode }) {
  return (
    <section className="space-y-5">
      <h3
        className={`display-upright text-[length:var(--text-kiosk-lg)] ${
          required === true ? 'text-[var(--color-bad)]' : ''
        }`}
      >
        {title}
      </h3>
      <div className="flex flex-wrap gap-4">{children}</div>
    </section>
  )
}

/**
 * One option, as a slab.
 *
 * A chip is enough for a mouse and is not enough here: a customer picking a
 * size is doing it with a thumb, at arm's length, without reading carefully.
 * The glyph carries the meaning, the border carries the state, and the whole
 * thing is a 72px target several times over.
 */
function Slab({
  selected,
  onSelect,
  label,
  glyph,
  price,
  accent
}: {
  selected: boolean
  onSelect: () => void
  label: string
  glyph?: string
  price?: bigint
  accent?: string
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      /*
        The accent arrives as a custom property, not as a `style` that
        overrides the class. Hot is red and iced is blue, and a chosen one has
        to be *filled* in its own colour rather than bordered in it — but when
        the style prop carried the fill directly it silently replaced the
        selected branch below, so the component had two definitions of
        "selected" and reading either one told you nothing.
      */
      style={accent === undefined ? undefined : ({ '--slab-accent': accent } as React.CSSProperties)}
      className={`tap flex flex-col items-center justify-center gap-3 rounded-[var(--radius-card)] border-4 px-6 py-8 ${
        !selected
          ? 'border-[var(--color-line)] bg-[var(--color-paper-raised)] text-[var(--color-ink-faint)]'
          : accent === undefined
            ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)]'
            : 'border-[color:var(--slab-accent)] bg-[color-mix(in_oklch,var(--slab-accent)_12%,white)]'
      }`}
    >
      {glyph !== undefined && (
        <span aria-hidden="true" className="text-6xl leading-none">
          {glyph}
        </span>
      )}
      <span className="display-upright text-[length:var(--text-kiosk-lg)] text-[var(--color-ink)] uppercase">
        {label}
      </span>
      {price !== undefined && price > 0n && (
        <span className="display-upright text-[var(--color-brand-strong)]">+{format(price as never)}</span>
      )}
    </button>
  )
}
